import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PermanentDiscountsService, PermanentDiscountItem } from '../../../services/permanent-discounts.service';
import { AnalyticsService } from '../../../services/analytics.service';

@Component({
  selector: 'app-permanent-discounts',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './permanent-discounts.component.html',
  styleUrls: ['./permanent-discounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PermanentDiscountsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('sectionElement', { static: false }) sectionElement?: ElementRef;

  permanentDiscounts: PermanentDiscountItem[] = [];
  isLoading = true;
  error = false;
  private destroy$ = new Subject<void>();
  private isInitialized = false; // Protección contra re-inicialización

  // Variables para drag scroll
  private isDown = false;
  private startX = 0;
  private scrollLeft = 0;

  constructor(
    private permanentDiscountsService: PermanentDiscountsService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    if (!this.isInitialized) {
      this.loadPermanentDiscounts();
      this.isInitialized = true;
    }
  }

  ngAfterViewInit() {
    // Registrar la sección para tracking de visibilidad
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.registerSectionForTracking(
        this.sectionElement.nativeElement,
        'Permanent Discounts',
        'permanent-discounts-section'
      );
    }

    // Registrar promociones para lazy loading después de que se rendericen
    setTimeout(() => {
      this.registerPromotionsForTracking();
    }, 100);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPermanentDiscounts() {
    console.log('🔄 Permanent Discounts: Iniciando carga de descuentos permanentes');

    this.isLoading = true;
    this.error = false;
    this.cdr.markForCheck();

    this.permanentDiscountsService.getPermanentDiscountsOrdered()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (discounts) => {
          console.log('✅ Permanent Discounts: Descuentos permanentes cargados exitosamente', discounts.length);
          console.log('📋 Descuentos:', discounts.map(d => ({ id: d.id, titulo: d.titulo, orden: d.orden })));

          this.permanentDiscounts = discounts;
          this.isLoading = false;
          this.cdr.markForCheck();

          // Registrar promociones después de cargar los datos
          setTimeout(() => {
            this.registerPromotionsForTracking();
          }, 100);
        },
        error: (error) => {
          console.error('❌ Permanent Discounts: Error cargando descuentos permanentes:', error);
          this.error = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  // Método para registrar todas las promociones para tracking
  private registerPromotionsForTracking(): void {
    this.permanentDiscounts.forEach((discount, index) => {
      const elementId = `permanent-${discount.id}`;
      const element = document.getElementById(elementId);
      if (element) {
        this.analyticsService.registerPromotionForTracking(
          element,
          elementId,
          discount.titulo || '',
          discount.img || '',
          `permanent_discount_${discount.orden || index}`
        );
      }
    });
  }

  // Método para forzar actualización
  refreshPermanentDiscounts() {
    console.log('🔄 Permanent Discounts: Forzando actualización');
    this.permanentDiscountsService.forceRefresh()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (discounts) => {
          console.log('✅ Permanent Discounts: Descuentos permanentes actualizados');
          this.permanentDiscounts = discounts.sort((a, b) => a.orden - b.orden);
          this.cdr.markForCheck();

          // Re-registrar promociones después de actualizar
          setTimeout(() => {
            this.registerPromotionsForTracking();
          }, 100);
        },
        error: (error) => {
          console.error('❌ Permanent Discounts: Error actualizando descuentos permanentes:', error);
        }
      });
  }

  // Método para obtener estadísticas
  getDiscountsStatistics() {
    return this.permanentDiscountsService.getDiscountsStatistics();
  }

  // Método para debugging - verificar estado del servicio
  getServiceStatus() {
    return this.permanentDiscountsService.getCacheStatus();
  }

  // Método para trackear items en el template
  trackByDiscountId(index: number, item: PermanentDiscountItem): number {
    return item.id;
  }

  // Método para manejar errores de imagen
  onImageError(event: Event, index: number, type: 'main' | 'logo'): void {
    const img = event.target as HTMLImageElement;

    // Evitar bucle infinito verificando si ya se intentó cargar el placeholder
    if (img.src.includes('placeholder.svg') || img.src.includes('data:image')) {
      return;
    }

    // Limitar el número de warnings para evitar spam en consola
    if (index < 3) {
      console.warn(`⚠️ Permanent Discounts: Error cargando imagen ${type} para descuento ${index + 1}`);
    }

    img.src = 'assets/placeholder.svg';
  }

  // Método para manejar clics en descuentos
  onDiscountClick(item: PermanentDiscountItem): void {
    if (item.url && item.url !== '#') {
      console.log('🖱️ Permanent Discounts: Clic en descuento', item.titulo, item.url);

      // Trackear el click
      const promotionId = `permanent-${item.id}`;
      const promotionName = item.titulo || '';
      const creativeName = item.img || '';
      const creativeSlot = `permanent_discount_${item.orden || 0}`;
      const deviceType = this.getCurrentDeviceType();

      const eventData = this.analyticsService.createPromotionEventData(
        promotionId,
        deviceType,
        promotionName,
        creativeName,
        creativeSlot
      );

      this.analyticsService.trackPromotionSelection(eventData);

      // Si es una URL externa, abrir en nueva pestaña
      if (item.url.startsWith('http')) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      } else {
        // Si es una ruta interna, usar router (si estuviera disponible)
        console.log('Navegación interna a:', item.url);
      }
    }
  }

  isDiscountClickable(item: PermanentDiscountItem): boolean {
    return !!(item.url && item.url !== '#');
  }

  // Método para obtener el tipo de dispositivo actual
  private getCurrentDeviceType(): string {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth;
    if (width >= 1281) return 'xl';
    if (width >= 1024) return 'desktop';
    if (width >= 768) return 'tablet';
    return 'movil';
  }

  // Métodos para drag scroll
  onMouseDown(event: MouseEvent): void {
    const carousel = event.currentTarget as HTMLElement;
    this.isDown = true;
    carousel.classList.add('active');
    this.startX = event.pageX - carousel.offsetLeft;
    this.scrollLeft = carousel.scrollLeft;
  }

  onMouseLeave(event: MouseEvent): void {
    const carousel = event.currentTarget as HTMLElement;
    this.isDown = false;
    carousel.classList.remove('active');
  }

  onMouseUp(event: MouseEvent): void {
    const carousel = event.currentTarget as HTMLElement;
    this.isDown = false;
    carousel.classList.remove('active');
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDown) return;
    event.preventDefault();
    const carousel = event.currentTarget as HTMLElement;
    const x = event.pageX - carousel.offsetLeft;
    const walk = (x - this.startX) * 2; // Multiplicador para velocidad del scroll
    carousel.scrollLeft = this.scrollLeft - walk;
  }

  onWheel(event: WheelEvent): void {
    const carousel = event.currentTarget as HTMLElement;
    event.preventDefault();
    carousel.scrollLeft += event.deltaY;
  }
}
